import InitialData from "../appData/initialData"
import {
  ActiveSubscription,
  LongestUsedSubscription,
  Plan,
  ProductData,
  ProductPromoPricing,
} from "../appData/productData"
import { QueryParams } from "../hooks/useQueryParams"
import { isPromo } from "./utils"

function regularPlans(
  premiumDiscounts: ProductPromoPricing[],
  ultimateDiscounts: ProductPromoPricing[],
) {
  const premium: Plan = {
    hasTrial: true,
    isUltimate: false,
    threeMonthsProduct: {
      discounts: [],
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      introPricing: {
        price: 0,
        period: 7,
        type: 0,
        identifier: "free_trial",
      },
      productId: "ppv_new_user_30_3months_mid_2022",
      price: 29.99,
      duration: "threeMonths",
      hasTrial: true,
    },
    yearlyProduct: {
      discounts: premiumDiscounts,
      priceLocale: {
        currency: "USD",
        language: "en-US",
      },
      introPricing: {
        price: 0,
        period: 7,
        type: 0,
        identifier: "free_trial",
      },
      duration: "yearly",
      hasTrial: true,
      productId: "ppv_new_user_36_yearly_mid_2022",
      price: 35.99,
    },
    monthlyProduct: {
      discounts: [],
      duration: "monthly",
      hasTrial: true,
      price: 13.99,
      introPricing: {
        price: 0,
        period: 3,
        type: 0,
        identifier: "free_trial",
      },
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      productId: "ppv_new_user_15_monthly_mid_2022",
    },
  }
  const ultimate: Plan = {
    hasTrial: true,
    isUltimate: true,
    yearlyProduct: {
      discounts: ultimateDiscounts,
      introPricing: {
        period: 7,
        price: 0,
        type: 0,
        identifier: "free_trial",
      },
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      duration: "yearly",
      hasTrial: true,
      productId: "ppv_new_user_36_yearly_mid_2022_ultimate",
      price: 119.99,
    },
    threeMonthsProduct: {
      discounts: [],
      introPricing: {
        period: 7,
        price: 0,
        type: 0,
        identifier: "free_trial",
      },
      price: 49.99,
      duration: "threeMonths",
      productId: "ppv_new_user_30_3months_mid_2022_ultimate",
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      hasTrial: true,
    },
    monthlyProduct: {
      discounts: [],
      hasTrial: true,
      price: 19.99,
      duration: "monthly",
      productId: "ppv_new_user_15_monthly_mid_2022_ultimate",
      introPricing: {
        price: 0,
        period: 3,
        type: 0,
        identifier: "free_trial",
      },
      priceLocale: {
        currency: "USD",
        language: "en-US",
      },
    },
  }

  return { premium, ultimate }
}

function proPlans(
  premiumDiscounts: ProductPromoPricing[],
  ultimateDiscounts: ProductPromoPricing[],
) {
  const premium: Plan = {
    hasTrial: true,
    isUltimate: false,
    threeMonthsProduct: {
      discounts: [],
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      price: 18.99,
      productId: "ppv_pro_3months_no_trial",
      duration: "threeMonths",
      hasTrial: false,
    },
    yearlyProduct: {
      discounts: premiumDiscounts,
      priceLocale: {
        currency: "USD",
        language: "en-US",
      },
      introPricing: {
        price: 0,
        period: 90,
        type: 0,
        identifier: "",
      },
      duration: "yearly",
      hasTrial: true,
      productId: "ppv_pro_yearly_3_month_trial",
      price: 39.99,
    },
    monthlyProduct: {
      discounts: [],
      duration: "monthly",
      hasTrial: false,
      price: 6.99,
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      productId: "ppv_pro_monthly_no_trial",
    },
  }
  const ultimate: Plan = {
    hasTrial: false,
    isUltimate: true,
    yearlyProduct: {
      discounts: ultimateDiscounts,
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      duration: "yearly",
      hasTrial: false,
      productId: "ppv_pro_yearly_3_month_trial_ultimate",
      price: 119.99,
    },
    threeMonthsProduct: {
      discounts: [],
      price: 49.99,
      duration: "threeMonths",
      productId: "ppv_pro_3months_no_trial_ultimate",
      priceLocale: {
        language: "en-US",
        currency: "USD",
      },
      hasTrial: false,
    },
    monthlyProduct: {
      discounts: [],
      hasTrial: false,
      price: 19.99,
      duration: "monthly",
      productId: "ppv_pro_monthly_no_trial_ultimate",
      priceLocale: {
        currency: "USD",
        language: "en-US",
      },
    },
  }

  return { premium, ultimate }
}

function promoPlans(
  premiumDiscounts: ProductPromoPricing[],
  ultimateDiscounts: ProductPromoPricing[],
) {
  const base = regularPlans(premiumDiscounts, ultimateDiscounts)

  base.premium.yearlyProduct.introPricing = {
    period: 365,
    price: 19.99,
    type: 0,
    identifier: "promo",
  }

  base.ultimate.yearlyProduct.introPricing = {
    period: 365,
    price: 59.99,
    type: 0,
    identifier: "promo",
  }

  return base
}

function getActiveSubscription(
  queryParams: QueryParams | null,
  premium: Plan,
  ultimate: Plan,
): ActiveSubscription | undefined {
  const sub = queryParams?.debugSubscription

  if (sub == null) {
    return undefined
  }

  const product =
    sub === "premium" ? premium.yearlyProduct : ultimate.yearlyProduct
  const tier = sub === "premium" ? "premium" : "ultimate"
  const period = "yearly"

  return {
    product,
    isActive: true,
    productId: product.productId,
    tier,
    period,
    purchase: Date.now() / 1000 - 100000,
    expiration: Date.now() / 1000 + 100000,
  }
}

function getLongestUsedSubscription(): LongestUsedSubscription {
  return {
    hasReducedRate: true,
    collection: {
      hasTrial: false,
      threeMonthsProduct: {
        price: 18.99,
        productId: "ppv_pro_3months_no_trial",
        priceLocale: {
          currency: "USD",
          language: "en-US",
        },
        hasTrial: false,
        discounts: [],
        duration: "threeMonths",
      },
      premium: {
        hasTrial: false,
        isUltimate: false,
        yearlyProduct: {
          hasTrial: true,
          priceLocale: {
            currency: "USD",
            language: "en-US",
          },
          productId: "ppv_pro_yearly_3_month_trial",
          discounts: [
            {
              price: 19.99,
              period: 365,
              type: 1,
              identifier: "ppv_pro_yearly_3_month_trial_blackfriday",
            },
            {
              type: 1,
              identifier: "ppv_pro_yearly_3_month_trial_promo_trial",
              period: 7,
              price: 0,
            },
          ],
          price: 39.99,
          introPricing: {
            price: 0,
            period: 90,
            type: 0,
            identifier: "",
          },
          duration: "yearly",
        },
        monthlyProduct: {
          hasTrial: false,
          priceLocale: {
            currency: "USD",
            language: "en-US",
          },
          productId: "ppv_pro_monthly_no_trial",
          discounts: [],
          price: 6.99,
          duration: "monthly",
        },
        threeMonthsProduct: {
          price: 18.99,
          productId: "ppv_pro_3months_no_trial",
          priceLocale: {
            currency: "USD",
            language: "en-US",
          },
          hasTrial: false,
          discounts: [],
          duration: "threeMonths",
        },
      },
      ultimate: {
        hasTrial: false,
        isUltimate: true,
        yearlyProduct: {
          price: 119.99,
          productId: "ppv_pro_yearly_3_month_trial_ultimate",
          priceLocale: {
            language: "en-US",
            currency: "USD",
          },
          hasTrial: false,
          discounts: [
            {
              period: 365,
              type: 1,
              identifier: "ppv_pro_yearly_3_month_trial_ultimate_blackfriday",
              price: 79.99,
            },
          ],
          duration: "yearly",
        },
        threeMonthsProduct: {
          productId: "ppv_pro_3months_no_trial_ultimate",
          discounts: [],
          price: 49.99,
          priceLocale: {
            currency: "USD",
            language: "en-US",
          },
          hasTrial: false,
          duration: "threeMonths",
        },
        monthlyProduct: {
          priceLocale: {
            language: "en-US",
            currency: "USD",
          },
          discounts: [],
          productId: "ppv_pro_monthly_no_trial_ultimate",
          price: 19.99,
          hasTrial: false,
          duration: "monthly",
        },
      },
      yearlyProduct: {
        hasTrial: true,
        priceLocale: {
          currency: "USD",
          language: "en-US",
        },
        productId: "ppv_pro_yearly_3_month_trial",
        discounts: [
          {
            price: 19.99,
            period: 365,
            type: 1,
            identifier: "ppv_pro_yearly_3_month_trial_blackfriday",
          },
          {
            type: 1,
            identifier: "ppv_pro_yearly_3_month_trial_promo_trial",
            period: 7,
            price: 0,
          },
        ],
        price: 39.99,
        introPricing: {
          price: 0,
          period: 90,
          type: 0,
          identifier: "",
        },
        duration: "yearly",
      },
      monthlyProduct: {
        hasTrial: false,
        priceLocale: {
          currency: "USD",
          language: "en-US",
        },
        productId: "ppv_pro_monthly_no_trial",
        discounts: [],
        price: 6.99,
        duration: "monthly",
      },
    },
    days: 4320,
  }
}

function getProductData(queryParams: QueryParams | null): ProductData {
  const hasTrial = queryParams?.hasTrial ?? false
  const hasDiscount =
    (queryParams?.hasDiscount ||
      queryParams?.postPurchaseFlow ||
      queryParams?.specialOffer) ??
    false
  const hasPromo = isPromo()
  const hasPro = queryParams?.hasPro ?? false
  const lastExpiredSubscriptionWasUltimate =
    queryParams?.lastExpiredSubscriptionWasUltimate ?? false

  let premiumDiscounts: ProductPromoPricing[] = []
  let ultimateDiscounts: ProductPromoPricing[] = []
  if (hasDiscount) {
    premiumDiscounts = [
      {
        price: 19.99,
        period: 365,
        type: 1,
        identifier: "pro.new.annual.mid.2022.promo",
      },
    ]
    ultimateDiscounts = [
      {
        price: 79.99,
        period: 365,
        type: 1,
        identifier: "pro.new.annual.mid.2022.ultimate.promo",
      },
    ]
  }

  const plans = hasPro
    ? proPlans(premiumDiscounts, ultimateDiscounts)
    : hasPromo
      ? promoPlans(premiumDiscounts, ultimateDiscounts)
      : regularPlans(premiumDiscounts, ultimateDiscounts)

  const longestUsedSubscription = queryParams?.debugLongestSubscription
    ? getLongestUsedSubscription()
    : undefined

  return {
    loaded: false,
    originalInstallDate: 1686595196,
    promoRemainingSeconds: 60 * 60 * 5,
    premium: plans.premium,
    ultimate: plans.ultimate,
    hasPINRecovery: false,
    reinstallDate: 1709832306,
    closeFadeTime: 0,
    hadAutomaticTrial: false,
    isForever: false,
    secondsSinceFirstUse: 30,
    numAlbums: 0,
    numPhotos: 0,
    products: [
      {
        discounts: [],
        introPricing: {
          period: 365,
          price: 19.99,
          type: 0,
          identifier: "free_trial",
        },
        priceLocale: {
          language: "en-US",
          currency: "USD",
        },
        productId: "promo_yearly",
        price: 35.99,
        hasTrial: false,
        duration: "yearly",
      },
    ],
    originalProducts: [],
    hasTrial,
    ultimatePaywallGlobalApplies: true,
    isUltimatePlacement: false,
    activeSubscription: getActiveSubscription(
      queryParams,
      plans.premium,
      plans.ultimate,
    ),
    longestUsedSubscription,
    hasPro,
    lastExpiredSubscriptionWasUltimate,
  }
}

function getInitialData(queryParams: QueryParams): InitialData {
  const darkMode = queryParams.darkMode
  const language = queryParams.language

  return {
    bottomInset: 20,
    originalBottomInset: 20,
    topInset: 40,
    originalTopInset: 40,
    darkMode,
    language,
    source: "albumListTop",
  }
}

export default { getProductData, getInitialData }
